'use client'

/**
 * The draft card's editor for a `priority` draft — the Plan step (#16815).
 *
 * Title, owner, due date and an optional parent. Each is the asker's to
 * change before Approve, and none is trusted by the server, which re-resolves
 * the owner and the parent inside the org.
 *
 *  - **Title** — required, at most `MAX_DRAFT_PRIORITY_TITLE_LENGTH`; the
 *    error is shown under the field and the card disables Approve on it
 *    (`priorityDraftError`).
 *  - **Owner** — the person picker the captain dropdowns use, over the org's
 *    active people (`getAllPeopleForSelection`). Nothing chosen is the
 *    viewer, which the trigger says: "You".
 *  - **Due** — a calendar day, or none.
 *  - **Parent** — the priorities the viewer can see (`getPriorityCandidates`,
 *    the priority browser's own read), or none.
 *
 * The two lists load once, on open. A list that fails to load leaves its
 * picker with only its default — the draft is still approvable.
 */

import { useEffect, useId, useState } from 'react'

import { getAllPeopleForSelection } from '@/app/actions/people-selection'
import {
  getPriorityCandidates,
  type PriorityCandidate,
} from '@/app/actions/priority-browser'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  PersonPicker,
  type PersonPickerOption,
} from '@/components/ui/person-picker'
import { SearchableSelect } from '@/components/ui/searchable-select'
import {
  type Draft,
  MAX_DRAFT_PRIORITY_TITLE_LENGTH,
  priorityDraftError,
} from '@/lib/ai-chat/next-steps'

type PriorityDraft = Extract<Draft, { kind: 'priority' }>

/** What the owner picker shows when nothing is chosen: the viewer. */
export const PRIORITY_DRAFT_OWNER_DEFAULT = 'You'

export function PriorityDraftEditor({
  draft,
  disabled,
  onChange,
}: {
  draft: PriorityDraft
  disabled: boolean
  onChange: (draft: Draft) => void
}) {
  const baseId = useId()
  const [people, setPeople] = useState<PersonPickerOption[]>([])
  const [parents, setParents] = useState<PriorityCandidate[]>([])

  useEffect(() => {
    let live = true
    getAllPeopleForSelection()
      .then(result => {
        if (live && result.success && 'people' in result) {
          setPeople(result.people)
        }
      })
      .catch(() => {
        // The owner stays the viewer.
      })
    getPriorityCandidates()
      .then(candidates => {
        if (live) setParents(candidates)
      })
      .catch(() => {
        // No parent offered; the draft is still approvable.
      })
    return () => {
      live = false
    }
  }, [])

  const titleError = priorityDraftError(draft)
  const titleErrorId = `${baseId}-title-error`

  function setOwner(id: string | null) {
    const { ownerPersonId: _id, ownerName: _name, ...rest } = draft
    if (id === null) {
      onChange(rest)
      return
    }
    const person = people.find(candidate => candidate.id === id)
    const name = person?.name || person?.email
    onChange({
      ...rest,
      ownerPersonId: id,
      ...(name ? { ownerName: name } : {}),
    })
  }

  function setDueDate(value: string) {
    const { dueDate: _due, ...rest } = draft
    onChange(value ? { ...rest, dueDate: value } : rest)
  }

  function setParent(id: string | null) {
    const { parentPriorityId: _parent, ...rest } = draft
    onChange(id ? { ...rest, parentPriorityId: id } : rest)
  }

  return (
    <div data-testid="ai-chat-draft-priority" className="mt-3 space-y-2">
      <div className="space-y-1">
        <Label htmlFor={`${baseId}-title`}>Title</Label>
        <Input
          id={`${baseId}-title`}
          value={draft.title}
          maxLength={MAX_DRAFT_PRIORITY_TITLE_LENGTH}
          disabled={disabled}
          aria-invalid={titleError !== null}
          aria-describedby={titleError ? titleErrorId : undefined}
          onChange={event => onChange({ ...draft, title: event.target.value })}
        />
        {titleError && (
          <p id={titleErrorId} className="text-xs text-text-secondary">
            {titleError}
          </p>
        )}
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${baseId}-owner`}>Owner</Label>
        <PersonPicker
          id={`${baseId}-owner`}
          value={draft.ownerPersonId ?? null}
          onChange={setOwner}
          people={people}
          placeholder={PRIORITY_DRAFT_OWNER_DEFAULT}
          clearLabel={PRIORITY_DRAFT_OWNER_DEFAULT}
          disabled={disabled}
          testId="ai-chat-draft-priority-owner"
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${baseId}-due`}>Due date</Label>
        <Input
          id={`${baseId}-due`}
          type="date"
          value={draft.dueDate ?? ''}
          disabled={disabled}
          onChange={event => setDueDate(event.target.value)}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${baseId}-parent`}>Supports</Label>
        <SearchableSelect<PriorityCandidate>
          id={`${baseId}-parent`}
          value={draft.parentPriorityId ?? null}
          onChange={setParent}
          items={parents}
          getId={candidate => candidate.id}
          getSearchText={candidate => candidate.title}
          placeholder="No parent priority"
          searchPlaceholder="Search priorities…"
          clearLabel="No parent priority"
          emptyLabel="No priorities found"
          disabled={disabled}
          testId="ai-chat-draft-priority-parent"
          renderSelected={candidate => (
            <span className="flex-1 truncate text-text-primary">
              {candidate.title}
            </span>
          )}
          renderOption={candidate => (
            <span className="block truncate text-text-primary">
              {candidate.title}
            </span>
          )}
        />
      </div>
    </div>
  )
}
